<script setup lang="ts">
import { onMounted, ref, reactive, computed } from 'vue'
// Определение размера экрана — для динамического padding
import { useDisplay } from 'vuetify'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
import { useAdminStore } from '@/modules/admin/store/adminStore'
import { storeToRefs } from 'pinia'
import SectionHeader from '@/components/SectionHeader.vue'
import type { IServerType } from '@/modules/admin/types/IServerType'
import type { ICreateServerPayload } from '@/modules/admin/types/ICreateServerPayload'
import type { IUpdateServerPayload } from '@/modules/admin/types/IUpdateServerPayload'

const adminStore = useAdminStore()
// Определение мобильного экрана для динамического padding
const { mobile } = useDisplay()
const { servers } = storeToRefs(adminStore)

// --- Логика создания сервера ---
const isCreateDialogOpen = ref(false)

const isDeleteDialogOpen = ref(false)
// Сервер, выбранный для удаления — нужен для подтверждения с названием в диалоге
const serverToDelete = ref<IServerType | null>(null)

const newServer = reactive<ICreateServerPayload>({
  country: '',
  city: '',
  adminPanelURL: '',
  isDefault: false,
  token: '',
  vlessURL: '',
  inboundID: undefined,
})

const openCreateDialog = () => {
  Object.assign(newServer, { country: '', city: '', adminPanelURL: '', isDefault: false, token: '', vlessURL: '', inboundID: undefined })
  isCreateDialogOpen.value = true
}

const createServer = async () => {
  const success = await adminStore.createServer({ ...newServer })
  if (success) isCreateDialogOpen.value = false
}

// --- Логика редактирования ---
const editingServerIds = ref<Set<string>>(new Set())
// Снапшоты редактируемых серверов — для отката при «Отмена». v-model правит объект в storeToRefs напрямую,
// без снапшота изменённые поля останутся в сторе даже при отмене
const editingSnapshots = new Map<string, Partial<IServerType>>()
// Новые токены редактируемых серверов (сервер токен не возвращает, поле только для записи)
const newTokens = reactive<Record<string, string>>({})

const isEditing = (id: string) => editingServerIds.value.has(id)

/**
 * Сохраняет копию редактируемых полей сервера — чтобы можно было откатить при отмене
 */
const saveSnapshot = (server: IServerType) => {
  editingSnapshots.set(server.id, {
    country: server.country,
    city: server.city,
    adminPanelURL: server.adminPanelURL,
    vlessURL: server.vlessURL,
    inboundID: server.inboundID,
    isDefault: server.isDefault,
  })
}

/**
 * Возвращает поля сервера к снапшоту (если он был сохранён)
 */
const restoreSnapshot = (server: IServerType) => {
  // Введённый, но не сохранённый токен тоже сбрасываем
  delete newTokens[server.id]
  const snap = editingSnapshots.get(server.id)
  if (!snap) return
  Object.assign(server, snap)
  editingSnapshots.delete(server.id)
}

// --- Раскрытие карточек (v-expansion-panels, multiple) ---
// Массив id открытых серверов — один сервер можно открыть в view, другой в edit одновременно
const expandedServerIds = ref<string[]>([])

/**
 * Переключает панель сервера в режиме редактирования: повторный клик сворачивает и выходит из edit.
 * Если панель открыта в просмотре — переводит её в edit (не сворачивая).
 */
const toggleEditMode = (server: IServerType) => {
  const isOpen = expandedServerIds.value.includes(server.id)
  const inEdit = editingServerIds.value.has(server.id)
  // Открыта в edit — откатываем несохранённые правки и сворачиваем
  if (isOpen && inEdit) {
    restoreSnapshot(server)
    expandedServerIds.value = expandedServerIds.value.filter((id) => id !== server.id)
    editingServerIds.value.delete(server.id)
    return
  }
  // Переход в edit — сохраняем снапшот для возможного отката
  saveSnapshot(server)
  editingServerIds.value.add(server.id)
  if (!isOpen) {
    expandedServerIds.value = [...expandedServerIds.value, server.id]
  }
}

/**
 * Отмена редактирования — откатывает несохранённые правки, выходит из режима редактирования и сворачивает панель.
 */
const cancelEdit = (server: IServerType) => {
  restoreSnapshot(server)
  editingServerIds.value.delete(server.id)
  expandedServerIds.value = expandedServerIds.value.filter((id) => id !== server.id)
}

// --- Логика переключения Default ---
const onDefaultChange = async (server: IServerType, value: boolean | null) => {
  await adminStore.updateServer({ id: server.id, isDefault: value === true })
}

// ID сервера, выбранного как дефолтный — для общей v-radio-group вокруг панелей
const defaultServerId = computed({
  get: () => servers.value?.find((s) => s.isDefault)?.id ?? '',
  set: async (id: string) => {
    const server = servers.value?.find((s) => s.id === id)
    if (!server) return
    await onDefaultChange(server, true)
  },
})

// --- Сохранение изменений ---
const saveServerChanges = async (server: IServerType) => {
  const payload: IUpdateServerPayload = {
    id: server.id,
    country: server.country,
    city: server.city,
    adminPanelURL: server.adminPanelURL,
    isDefault: server.isDefault,
    vlessURL: server.vlessURL,
    inboundID: server.inboundID,
    // Токен отправляем только если его ввели — пустое поле оставляет прежний
    token: newTokens[server.id] || undefined,
  }

  const success = await adminStore.updateServer(payload)
  if (success) {
    delete newTokens[server.id]
    // Успешно сохранили — снапшот больше не нужен, выходим из edit и сворачиваем панель
    editingSnapshots.delete(server.id)
    editingServerIds.value.delete(server.id)
    expandedServerIds.value = expandedServerIds.value.filter((id) => id !== server.id)
  }
}

// --- Удаление ---
const deleteServer = (server: IServerType) => {
  serverToDelete.value = server
  isDeleteDialogOpen.value = true
}

const confirmDelete = async () => {
  if (!serverToDelete.value) return

  await adminStore.deleteServer(serverToDelete.value.id)

  serverToDelete.value = null
  isDeleteDialogOpen.value = false
}

/**
 * Обновляет данные сервера с бэкенда по текущему inboundID
 * Отправляет updateServer с id и inboundID, затем перезагружает список серверов
 *
 * @param {IServerType} server - Объект сервера для обновления
 */
const refreshServerData = async (server: IServerType) => {
  const success = await adminStore.updateServer({
    id: server.id,
    inboundID: server.inboundID,
  })
  if (success) await adminStore.getServers()
}

// --- Вспомогательные функции ---
const openLink = (url: string) => {
  if (url) window.open(url, '_blank')
}

/**
 * Вырезает Unicode regional-indicator символы (эмодзи-флаги) из строки страны.
 * Бэк отдаёт country с эмодзи-флагом, в админке нужно только текстовое название.
 */
const stripFlag = (country: string) =>
  country.replace(/[\u{1F1E6}-\u{1F1FF}]/gu, '').trim()

const getPingColorClass = (ping: number) => {
  if (ping < 100) return 'text-success'
  if (ping < 250) return 'text-warning'
  return 'text-error'
}

const getPingChipColor = (ping: number) => {
  if (ping < 100) return 'success'
  if (ping < 250) return 'warning'
  return 'error'
}

onMounted(async () => {
  await adminStore.getServers()
})
</script>

<template>
  <div class="d-flex flex-column ga-6 pt-4" :class="{ 'pa-4': isTelegram() || mobile }">
    <header class="d-flex justify-space-between align-center">
      <section-header title="Управление серверами" />
    </header>

    <section class="d-flex flex-column ga-2">
      <div v-if="!servers || servers.length === 0" class="text-center text-text-disabled pa-4">
        Список серверов пуст
      </div>

      <v-btn
        block
        color="primary"
        variant="outlined"

        prepend-icon="ri-add-line"
        @click="openCreateDialog"
      >
        Добавить сервер
      </v-btn>

      <!-- Радио-группа выбора дефолтного сервера: один из всех — current default, переключение через клик -->
      <v-radio-group v-model="defaultServerId" hide-details density="compact" class="ma-0 pa-0">
      <v-expansion-panels v-model="expandedServerIds" multiple variant="accordion">
        <v-expansion-panel
          v-for="server in servers"
          :key="server.id"
          :value="server.id"
          bg-color="surface"
          rounded="lg"
          class="mb-2"
        >
          <!-- Заголовок карточки сервера: страна город + радио дефолт + иконки действий (карандаш/корзина) -->
          <v-expansion-panel-title hide-actions>
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center ga-2 flex-wrap">
                <span class="text-body-1">{{ stripFlag(server.country) }} {{ server.city }}</span>
                <!-- Метрики пинга и нагрузки -->
                <v-chip
                  v-if="server.pingMs !== undefined"
                  :color="getPingChipColor(server.pingMs)"
                  size="x-small"
                  variant="tonal"
                  prepend-icon="ri-pulse-line"
                >
                  {{ server.pingMs }} мс
                </v-chip>
                <v-chip
                  v-if="server.activeUsersCount !== undefined"
                  color="info"
                  size="x-small"
                  variant="tonal"
                  prepend-icon="ri-user-line"
                >
                  {{ server.activeUsersCount }}
                </v-chip>
              </div>
              <!-- Блок действий: радио дефолта + редактирование + удаление, плотной группой справа -->
              <div class="d-flex align-center ga-4">
                <v-radio
                  :value="server.id"
                  color="success"
                  hide-details
                  density="compact"
                  @click.stop
                />
                <v-tooltip text="Редактировать" location="top">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="ri-pencil-line"
                      variant="text"
                      size="small"
                      color="primary"
                      @click.stop="toggleEditMode(server)"
                    />
                  </template>
                </v-tooltip>
                <v-tooltip text="Удалить" location="top">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="ri-delete-bin-line"
                      variant="text"
                      size="small"
                      color="error"
                      @click.stop="deleteServer(server)"
                    />
                  </template>
                </v-tooltip>
              </div>
            </div>
          </v-expansion-panel-title>

          <!-- Содержимое карточки — форма с полями; readonly в режиме просмотра, активные в режиме редактирования -->
          <v-expansion-panel-text>
            <v-form @submit.prevent="saveServerChanges(server)">
              <div class="d-flex flex-column ga-2">
                <!-- Метрики сервера (Пинг и Нагрузка) -->
                <div class="d-flex flex-column ga-3 mb-3 pa-3 rounded-lg border-opacity-25" style="border: 1px solid rgba(var(--v-theme-on-surface), 0.12); background-color: rgba(var(--v-theme-on-surface), 0.02)">
                  <div class="d-flex flex-column ga-1">
                    <div class="d-flex justify-space-between align-center text-caption text-medium-emphasis">
                      <span>Нагрузка (активные пользователи)</span>
                      <span class="font-weight-bold text-white">{{ server.activeUsersCount !== undefined ? server.activeUsersCount : 0 }} / 500</span>
                    </div>
                    <v-progress-linear
                      :model-value="server.activeUsersCount !== undefined ? (server.activeUsersCount / 5) : 0"
                      color="primary"
                      height="8"
                      rounded
                    />
                  </div>

                  <div class="d-flex justify-space-between align-center text-caption text-medium-emphasis mt-1">
                    <span>Пинг до сервера</span>
                    <span v-if="server.pingMs !== undefined" :class="getPingColorClass(server.pingMs)" class="font-weight-bold">
                      {{ server.pingMs }} мс
                    </span>
                    <span v-else class="text-medium-emphasis">нет данных</span>
                  </div>
                </div>

                <v-text-field
                  v-model="server.id"
                  label="ID"
                  disabled
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  v-model="server.country"
                  label="Страна"
                  :readonly="!isEditing(server.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <v-text-field
                  v-model="server.city"
                  label="Город"
                  :readonly="!isEditing(server.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  v-model="server.adminPanelURL"
                  label="Admin Panel URL"
                  :readonly="!isEditing(server.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                >
                  <template #append-inner>
                    <v-btn
                      v-if="server.adminPanelURL"
                      prepend-icon="ri-external-link-line"
                      variant="text"
                      color="primary"
                      @click="openLink(server.adminPanelURL)"
                    />
                  </template>
                </v-text-field>

                <v-text-field
                  v-model="server.vlessURL"
                  label="VLESS URL"
                  :readonly="!isEditing(server.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <v-text-field
                  v-if="isEditing(server.id)"
                  v-model="newTokens[server.id]"
                  label="Новый Bearer-токен 3X-UI (пусто — не менять)"
                  type="password"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <v-text-field
                  v-model="server.vlessQuery"
                  label="VLESS Query (readonly)"
                  readonly
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <v-text-field
                  v-model.number="server.inboundID"
                  label="Inbound ID"
                  type="number"
                  :readonly="!isEditing(server.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                >
                  <template #append-inner>
                    <v-btn
                      variant="text"
                      color="primary"
                      density="compact"
                      prepend-icon="ri-refresh-line"
                      @click="refreshServerData(server)"
                    >
                      Обновить
                    </v-btn>
                  </template>
                </v-text-field>
                <v-text-field
                  v-model="server.port"
                  label="Port (readonly)"
                  type="number"
                  readonly
                  density="compact"
                  variant="outlined"
                  hide-details
                />

                <!-- Кнопки сохранения и отмены — только в режиме редактирования, в колонку -->
                <div v-if="isEditing(server.id)" class="d-flex flex-column ga-2">
                  <v-btn
                    color="success"
                    variant="flat"
                    type="submit"
                    block
                  >
                    Сохранить
                  </v-btn>
                  <v-btn
                    color="secondary"
                    variant="text"
                    block
                    @click="cancelEdit(server)"
                  >
                    Отмена
                  </v-btn>
                </div>
              </div>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      </v-radio-group>
    </section>

    <v-dialog v-model="isCreateDialogOpen" max-width="600px" :fullscreen="mobile">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div>Добавить новый сервер</div>
          <VIconBtn icon="ri-close-line" @click="isCreateDialogOpen = false" />
        </v-card-title>
        <v-card-text class="d-flex flex-column ga-2">
          <v-form @submit.prevent="createServer" class="d-flex flex-column ga-2">
            <v-text-field
              v-model="newServer.country"
              label="Страна"
              variant="outlined"
              hide-details
            />
            <v-text-field v-model="newServer.city" label="Город" variant="outlined" hide-details />
            <v-text-field
              v-model="newServer.adminPanelURL"
              label="Admin URL"
              variant="outlined"
              hide-details
            />
            <v-text-field
              v-model="newServer.token"
              label="Bearer-токен 3X-UI"
              type="password"
              variant="outlined"
              hide-details
            />
            <v-text-field
              v-model="newServer.vlessURL"
              label="VLESS URL"
              variant="outlined"
              hide-details
            />
            <!-- ID inbound-правила в 3X-UI (optional) -->
            <v-text-field
              v-model.number="newServer.inboundID"
              label="Inbound ID"
              type="number"
              variant="outlined"
              hide-details
            />
            <v-switch
              v-model="newServer.isDefault"
              label="Сделать основным"
              color="primary"
              hide-details
            />

            <div :class="['d-flex ga-2', mobile ? 'flex-column-reverse' : 'justify-end']">
              <v-btn color="secondary" variant="text" @click="isCreateDialogOpen = false"
                >Отмена</v-btn
              >
              <v-btn color="primary" variant="outlined" type="submit">Создать</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteDialogOpen" max-width="420">
      <v-card class="pa-5 d-flex flex-column ga-4" rounded="xl">
        <div class="text-h6">Удаление сервера</div>
        <div>
          Вы уверены, что хотите удалить сервер
          <span v-if="serverToDelete" class="font-weight-bold">{{ serverToDelete.country }}, {{ serverToDelete.city }}</span>?
        </div>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" @click="isDeleteDialogOpen = false">Отмена</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">Удалить</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.admin-servers__actions {
  min-width: 200px;
}
</style>
